import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import type { VendorWithRelations } from '@peles/shared';
import { useVendors } from '../../hooks/useVendors';

export interface VendorAutosuggestProps {
  value: string;
  vendorId: string | null;
  onChange: (vendorName: string, vendorId: string | null) => void;
  onCreateVendor?: (name: string) => void;
}

export function VendorAutosuggest({
  value,
  vendorId,
  onChange,
  onCreateVendor,
}: VendorAutosuggestProps) {
  const { data: vendors, isLoading } = useVendors({ isActive: true });
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const filteredVendors = useMemo(() => {
    if (!vendors || value.length < 2) return [];
    const lower = value.toLowerCase();
    return vendors.filter((v) => v.businessName.toLowerCase().includes(lower));
  }, [vendors, value]);

  const selectedVendor = useMemo(() => {
    if (!vendorId || !vendors) return null;
    return vendors.find((v) => v.id === vendorId) || null;
  }, [vendorId, vendors]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const text = e.target.value;
      onChange(text, null);
      setIsOpen(true);
      setHighlightedIndex(-1);
    },
    [onChange],
  );

  const handleSelect = useCallback(
    (vendor: VendorWithRelations) => {
      onChange(vendor.businessName, vendor.id);
      setIsOpen(false);
      setHighlightedIndex(-1);
    },
    [onChange],
  );

  const handleFocus = useCallback(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    if (value.length >= 2) {
      setIsOpen(true);
    }
  }, [value]);

  const handleBlur = useCallback(() => {
    blurTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }, 200);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) return;

      const totalItems = filteredVendors.length + (onCreateVendor ? 1 : 0);

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredVendors.length) {
          handleSelect(filteredVendors[highlightedIndex]);
        } else if (highlightedIndex === filteredVendors.length && onCreateVendor && value.trim()) {
          onCreateVendor(value.trim());
          setIsOpen(false);
        }
      } else if (e.key === 'Escape') {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    },
    [isOpen, filteredVendors, highlightedIndex, handleSelect, onCreateVendor, value],
  );

  const handleCopy = useCallback((text: string) => {
    void navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const items = dropdownRef.current.querySelectorAll('li');
      const item = items[highlightedIndex];
      if (item && typeof item.scrollIntoView === 'function') {
        item.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  // Clean up blur timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  const showDropdown =
    isOpen && !isLoading && value.length >= 2 && (filteredVendors.length > 0 || !!onCreateVendor);

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder="Search or type vendor name"
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        role="combobox"
        aria-expanded={showDropdown}
        aria-autocomplete="list"
        aria-controls="vendor-autosuggest-list"
        autoComplete="off"
      />

      {showDropdown && (
        <ul
          id="vendor-autosuggest-list"
          ref={dropdownRef}
          role="listbox"
          className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {filteredVendors.map((vendor, index) => (
            <li
              key={vendor.id}
              role="option"
              aria-selected={highlightedIndex === index}
              className={`px-3 py-2 cursor-pointer text-sm ${
                highlightedIndex === index
                  ? 'bg-primary-50 text-primary-900'
                  : 'hover:bg-gray-50 text-gray-900'
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(vendor);
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <div className="font-medium">{vendor.businessName}</div>
              {(vendor.city || vendor.phone) && (
                <div className="text-xs text-gray-500">
                  {[vendor.city, vendor.phone].filter(Boolean).join(' \u00B7 ')}
                </div>
              )}
            </li>
          ))}
          {onCreateVendor && value.trim() && (
            <li
              role="option"
              aria-selected={highlightedIndex === filteredVendors.length}
              className={`px-3 py-2 cursor-pointer text-sm border-t border-gray-100 ${
                highlightedIndex === filteredVendors.length
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-primary-600 hover:bg-gray-50'
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                onCreateVendor(value.trim());
                setIsOpen(false);
              }}
              onMouseEnter={() => setHighlightedIndex(filteredVendors.length)}
            >
              + Create New Vendor &ldquo;{value.trim()}&rdquo;
            </li>
          )}
        </ul>
      )}

      {selectedVendor && (selectedVendor.phone || selectedVendor.email) && (
        <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
          {selectedVendor.phone && (
            <button
              type="button"
              onClick={() => handleCopy(selectedVendor.phone!)}
              className="hover:text-gray-700"
              title="Copy phone"
            >
              {selectedVendor.phone}
            </button>
          )}
          {selectedVendor.phone && selectedVendor.email && <span>|</span>}
          {selectedVendor.email && (
            <button
              type="button"
              onClick={() => handleCopy(selectedVendor.email!)}
              className="hover:text-gray-700"
              title="Copy email"
            >
              {selectedVendor.email}
            </button>
          )}
          {copied && <span className="text-green-600 ml-1">Copied!</span>}
        </div>
      )}
    </div>
  );
}
